import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import JsBarcode from "jsbarcode";

interface Requirement {
  item: string;
  rate: number;
  subsidy: number;
  quantity: number;
  amount: number;
}

interface Financials {
  subTotal: number;
  subsidiary: number;
  grossAmount: number;
  discount: number;
  paid: number;
  due: number;
}

interface BloodBankData {
  hospitalName: string;
  hospitalLocation: string;
  hospitalDetails: string;
  slipNo: string;
  mrNo: string;
  refMrn?: string;
  patientName: string;
  gender: string;
  age: string;
  bloodGroup: string;
  rhFactor: string;
  hospital: string;
  phone: string;
  date: string;
  delivery: string;
  patientType: string;
  du: string;
  outSideRef: string;
  requirements: Requirement[];
  financials: Financials;
  remarks: string;
  notes: string[];
  discountedBy: string;
  pageHeaderData?: {
    _id: string;
    address: string;
    logo: string;
    phone: string;
    title: string;
  };
}

export const generateBloodBankSlipPDF = (data: BloodBankData) => {
  // Log the entire data object to the console
  console.log("BloodBankData:", JSON.stringify(data, null, 2));

  const doc = new jsPDF();
  doc.setFontSize(12);

  // Check if data exists
  if (!data || !data.pageHeaderData) {
    doc.text("No data available to generate PDF.", 10, 10);
    doc.save("blood_bank_slip.pdf");
    return;
  }

  // Extract pageHeaderData
  const pageHeaderData = data.pageHeaderData || {};
  const headerLogoUrl = pageHeaderData.logo || "";
  const headerTitle = pageHeaderData.title || "";
  const headerAddress = pageHeaderData.address || "";
  const headerPhone = pageHeaderData.phone || "";

  // Add header only if any data is available
  if (headerLogoUrl || headerTitle || headerAddress || headerPhone) {
    const pageWidth = doc.internal.pageSize.width;

    // Header dimensions
    const logoWidth = 20;
    const logoHeight = 20;
    const logoTextSpacing = 0;
    const headerY = 10;

    // Calculate text widths for centering
    let titleWidth = 0;
    let addressWidth = 0;

    if (headerTitle) {
      doc.setFont("times", "bold");
      doc.setFontSize(18);
      titleWidth = doc.getTextWidth(headerTitle.toUpperCase());
    }

    if (headerAddress || headerPhone) {
      doc.setFont("times", "bold");
      doc.setFontSize(8);
      const addressText = `${headerAddress?.trim() || ""} (Ph: ${
        headerPhone?.trim() || ""
      })`;
      addressWidth = doc.getTextWidth(addressText);
    }

    // Calculate total header width (logo + spacing + max text width)
    const maxTextWidth = Math.max(titleWidth, addressWidth);
    const totalHeaderWidth =
      (headerLogoUrl ? logoWidth + logoTextSpacing : 0) + maxTextWidth;

    // Calculate starting X position to center the header
    const headerStartX = (pageWidth - totalHeaderWidth) / 2;

    // Add logo if available (positioned for centered header)
    if (headerLogoUrl) {
      try {
        const logoX = headerStartX;
        doc.addImage(
          headerLogoUrl,
          "PNG",
          logoX,
          headerY,
          logoWidth,
          logoHeight
        );
      } catch (error) {
        console.error("Error adding logo:", error);
      }
    }

    // Add header text positioned to the right of logo (or centered if no logo)
    if (headerTitle || headerAddress || headerPhone) {
      const textStartX = headerLogoUrl
        ? headerStartX + logoWidth + logoTextSpacing
        : headerStartX;

      // --- Add Title ---
      if (headerTitle) {
        doc.setFont("times", "bold");
        doc.setFontSize(18);
        const titleText = headerTitle.toUpperCase();
        doc.text(titleText, textStartX, headerY + 10);

        // Add horizontal line under the title (matching title width)
        doc.setDrawColor(0);
        doc.setLineWidth(0.8);
        doc.line(
          textStartX,
          headerY + 12,
          textStartX + titleWidth,
          headerY + 12
        );
      }

      // --- Add Address + Phone ---
      if (headerAddress || headerPhone) {
        doc.setFont("times", "bold");
        doc.setFontSize(8);
        const addressText = `${headerAddress?.trim() || ""} (Ph: ${
          headerPhone?.trim() || ""
        })`;
        doc.text(addressText, textStartX, headerY + 17);

        // Add line under address (matching address width)
        doc.setDrawColor(0);
        doc.setLineWidth(0.1);
        doc.line(
          textStartX,
          headerY + 18,
          textStartX + addressWidth - 23,
          headerY + 18
        );
      }
    }
  }

  // Add horizontal line to separate header
  doc.setDrawColor(0);
  doc.line(10, 45, doc.internal.pageSize.width - 10, 45);

  // Barcode
  const barcodeCanvas = document.createElement("canvas");
  JsBarcode(barcodeCanvas, data.slipNo || "N/A", {
    format: "CODE128",
    displayValue: false,
    width: 2,
    height: 30,
  });
  const barcodeImage = barcodeCanvas.toDataURL("image/png");

  // Place barcode below the line, aligned with 'Age' label
  doc.addImage(barcodeImage, "PNG", 160, 50, 40, 12);

  // Helper function
  const createInfoLine = (
    key: string,
    value: string | number | undefined | null,
    y: number,
    keyX = 15,
    valueX = 50
  ) => {
    doc.setFont("helvetica", "bold");
    doc.text(`${key}:`, keyX, y);
    doc.setFont("helvetica", "normal");
    const safeValue =
      value !== undefined && value !== null ? String(value) : "N/A";
    doc.text(safeValue, valueX, y);
  };

  // Patient Info (Left Column)
  createInfoLine("Slip No", data.slipNo, 60);
  doc.setFontSize(10);
  createInfoLine("MR No", data.mrNo, 66);
  doc.setFontSize(10);
  createInfoLine("Ref MRN", data.refMrn || "--", 72);
  doc.setFontSize(10);
  createInfoLine("Patient Name", data.patientName, 78);
  doc.setFontSize(10);
  createInfoLine("Gender", data.gender, 84);
  doc.setFontSize(10);
  createInfoLine("Blood Group", `${data.bloodGroup} ${data.rhFactor}`, 90);
  doc.setFontSize(10);

  // Right Column
  createInfoLine("Age", data.age, 60, 110, 135);
  doc.setFontSize(10);
  createInfoLine("Phone", data.phone, 66, 110, 135);
  doc.setFontSize(10);
  createInfoLine("Date", data.date, 72, 110, 135);
  doc.setFontSize(10);
  createInfoLine("Delivery", data.delivery, 78, 110, 135);
  doc.setFontSize(10);
  createInfoLine("Hospital", data.hospital, 84, 110, 135);
  doc.setFontSize(10);

  // Separator
  doc.line(10, 106, 200, 106);

  // Requirements
  autoTable(doc, {
    startY: 121,
    head: [["Requirement(s)", "Rate", "Quantity", "Total Amount"]],
    body: data.requirements.map((req) => [
      req.item || "N/A",
      (req.rate || 0).toFixed(2),
      req.quantity,
      (req.amount || 0).toFixed(2),
    ]),
    theme: "grid",
    headStyles: { fillColor: "gray" },
  });

  // Remarks
  const remarksY = (doc as any).lastAutoTable.finalY + 10;
  doc.text("Remarks:", 15, remarksY);
  doc.rect(15, remarksY + 5, 80, 30);
  const wrappedRemarks = doc.splitTextToSize(
    data.remarks || "No remarks available",
    75
  );
  doc.text(wrappedRemarks, 20, remarksY + 10);

  // Financials
  const financialDetails = [
    ["Gross Amount", (data.financials.grossAmount || 0).toFixed(2)],
    ["Discount", (data.financials.discount || 0).toFixed(2)],
    [
      "Payable",
      (data.financials.grossAmount - data.financials.discount || 0).toFixed(2),
    ],
  ];

  autoTable(doc, {
    startY: remarksY,
    head: [["Description", "Value"]],
    body: financialDetails,
    theme: "plain",
    columnStyles: { 1: { fontStyle: "bold" } },
    margin: { left: 150 },
  });

  // Notes
  const notesY = (doc as any).lastAutoTable.finalY + 10;
  doc.text("Notes:", 15, notesY);
  let noteY = notesY + 6;
  data.notes.forEach((note, index) => {
    const wrappedNote = doc.splitTextToSize(
      `${index + 1}. ${note || "N/A"}`,
      180
    );
    doc.text(wrappedNote, 15, noteY);
    noteY += wrappedNote.length * 5;
  });

  // Open PDF in new window and trigger print
  const pdfBlob = doc.output("blob");
  const blobUrl = URL.createObjectURL(pdfBlob);

  // Create a hidden iframe
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  document.body.appendChild(iframe);

  // Wait for iframe to load PDF and trigger print
  iframe.onload = () => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
  };

  // Set the PDF blob URL as the iframe's source
  iframe.src = blobUrl;
};
